import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import portalApi, { apiError } from './portalApi';
import { T, sans } from './portalTheme';
import {
  PortalAppShell, PageContainer, PageHeader, Card, CardHeader, Button, Pill, Avatar, EmptyState, Spinner, Alert, Icon, IC,
  fmtDate, daysUntil, usePortalRemediationList, isRemediationUnseen, PortalRemediationRow,
} from './PortalWidgets';

type Status = 'in_progress' | 'awaiting_review' | 'completed';

interface RequestRow extends PortalRemediationRow {
  status: Status;
  title: string;
  vendor_name: string | null;
  sender_org_name: string | null;
  due_date: string | null;
  overdue: boolean;
  risk_count: number;
  open_risk_count: number;
  fixed_risk_count: number;
  categories: string[];
  current_score: number | null;
  projected_score: number | null;
  message: string | null;
  created_at: string | null;
  submitted_at: string | null;
  completed_at: string | null;
  last_message_by: 'tenant' | 'vendor' | null;
}
interface Risk {
  id: string; name: string; category: string | null; severity: string | null; current_severity: string | null; status: 'open' | 'fixed';
  description: string | null; recommendation: string | null; evidence: string | null; fixed_at: string | null;
}
interface Message { id: string; author_type: 'tenant' | 'vendor'; author_name: string | null; author_email: string | null; body: string; created_at: string }
interface Event { id: string; kind: string; actor: string | null; note: string | null; created_at: string }
interface Detail { request: RequestRow; risks: Risk[]; messages: Message[]; events: Event[] }

const BASE = '/v1/questionnaire-responses/portal/remediation';
const STATUS: Record<Status, { label: string; color: string; bg: string }> = {
  in_progress: { label: 'In progress', color: T.primary, bg: T.blueBg },
  awaiting_review: { label: 'Submitted for review', color: T.violet, bg: T.violetBg },
  completed: { label: 'Completed', color: T.green, bg: T.greenBg },
};
const SEVERITY: Record<string, string> = { Critical: T.red, High: '#EA580C', Medium: T.amber };
const EVENT_TEXT: Record<string, string> = {
  created: 'Request received',
  submitted: 'Submitted for review',
  reopened: 'Sent back for more work',
  completed: 'Marked completed',
  risk_fixed: 'Confirmed fixed by a scan',
  risk_reopened: 'Detected again by a scan',
  all_fixed: 'Every risk confirmed fixed by a scan',
};

const fmtDateTime = (iso: string) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

// /questionnaire/portal/remediation (every request sent to this email) and /questionnaire/portal/remediation/:requestId
// (one request: the risks to fix, whether scans confirm them fixed, the conversation, and Submit for review).
export default function PortalRemediation() {
  const { requestId } = useParams<{ requestId?: string }>();
  return (
    <PortalAppShell>
      <PageContainer>{requestId ? <RequestView requestId={requestId} /> : <ListView />}</PageContainer>
    </PortalAppShell>
  );
}

function StatusBadge({ status }: { status: Status }) {
  const meta = STATUS[status] || STATUS.in_progress;
  return <Pill color={meta.color} bg={meta.bg}>{meta.label}</Pill>;
}

function DueText({ row }: { row: RequestRow }) {
  if (!row.due_date) return <span style={{ fontSize: 13, color: T.faint }}>No due date</span>;
  const left = daysUntil(row.due_date);
  const open = row.status === 'in_progress';
  return (
    <div>
      <div style={{ fontSize: 13, color: T.ink2, fontWeight: 500 }}>{fmtDate(row.due_date)}</div>
      {open && left < 0 && <div style={{ fontSize: 11.5, fontWeight: 700, color: T.red, marginTop: 2 }}>Overdue by {-left} day{left === -1 ? '' : 's'}</div>}
      {open && left >= 0 && left <= 7 && <div style={{ fontSize: 11.5, fontWeight: 700, color: T.amber, marginTop: 2 }}>{left === 0 ? 'Due today' : `Due in ${left} day${left === 1 ? '' : 's'}`}</div>}
    </div>
  );
}

/* ─── List ─────────────────────────────────────────────────────────── */

function ListView() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'open' | 'completed' | 'all'>('open');
  const query = usePortalRemediationList<RequestRow>();
  const rows = query.data ?? [];
  const open = rows.filter(r => r.status !== 'completed');
  const shown = tab === 'open' ? open : tab === 'completed' ? rows.filter(r => r.status === 'completed') : rows;
  const waiting = rows.filter(r => r.status === 'in_progress').length;
  const unseen = rows.filter(isRemediationUnseen).length;

  return (
    <>
      <PageHeader title="Remediation" subtitle="Security risks that companies you work with have asked you to fix. Fixes are confirmed automatically when they next scan you." />
      {query.isPending ? (
        <Card><div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner size={28} /></div></Card>
      ) : query.isError ? (
        <Card><EmptyState icon={IC.alert} title="Couldn't load your remediation requests" action={<Button variant="secondary" onClick={() => query.refetch()}>Try again</Button>}>
          {apiError(query.error, 'Something went wrong.')}
        </EmptyState></Card>
      ) : rows.length === 0 ? (
        <Card><EmptyState icon={IC.shield} title="No remediation requests">When a company asks you to fix security risks, the request appears here.</EmptyState></Card>
      ) : (
        <Card padding={0}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '12px 16px', borderBottom: `1px solid ${T.bdr}`, flexWrap: 'wrap' }}>
            {([['open', 'Open', open.length], ['completed', 'Completed', rows.length - open.length], ['all', 'All', rows.length]] as const).map(([key, label, count]) => (
              <button key={key} onClick={() => setTab(key)} className={tab === key ? undefined : 'pw-nav'} style={{
                padding: '6px 12px', borderRadius: 99, border: 'none', cursor: 'pointer', fontFamily: sans, fontSize: 13, fontWeight: 600,
                background: tab === key ? T.blueBg : 'transparent', color: tab === key ? T.primary : T.slate,
              }}>{label} <span style={{ fontSize: 11.5, opacity: 0.8 }}>{count}</span></button>
            ))}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
              {unseen > 0 && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: T.red }}>
                  <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: T.red }} />
                  {unseen} new
                </span>
              )}
              {waiting > 0 && <span style={{ fontSize: 12.5, color: T.muted }}>{waiting} waiting on you</span>}
            </div>
          </div>
          {shown.length === 0 ? (
            <EmptyState icon={IC.checkCircle} title={tab === 'open' ? 'Nothing open' : 'Nothing here yet'}>{tab === 'open' ? 'Every request has been completed.' : null}</EmptyState>
          ) : shown.map(r => {
            const unread = isRemediationUnseen(r);
            return (
            <div key={r.id} className="pw-row" role="link" tabIndex={0} onClick={() => navigate(`/questionnaire/portal/remediation/${r.id}`)}
              onKeyDown={e => { if (e.key === 'Enter') navigate(`/questionnaire/portal/remediation/${r.id}`); }}
              style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 20px', padding: '14px 16px',
                borderBottom: `1px solid ${T.bdr}`, cursor: 'pointer', background: unread ? T.redBg : 'transparent' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0, flex: '1 1 280px' }}>
                <Avatar name={r.sender_org_name || '?'} size={36} square />
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {unread && <span aria-hidden title="New" style={{ width: 7, height: 7, borderRadius: '50%', background: T.red, flexShrink: 0 }} />}
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</div>
                  </div>
                  <div style={{ fontSize: 12.5, color: T.muted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    From {r.sender_org_name} · for {r.vendor_name}{r.last_message_by === 'tenant' && r.status !== 'completed' ? ' · new message' : ''}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                {unread && <Pill color={T.red} bg={T.redBg}>New</Pill>}
                <div style={{ width: 110, fontSize: 13, color: r.open_risk_count ? T.ink2 : T.green, fontWeight: r.open_risk_count ? 500 : 600 }}>
                  {r.open_risk_count ? `${r.open_risk_count} of ${r.risk_count} open` : `All ${r.risk_count} fixed`}
                </div>
                <div style={{ width: 120 }}><DueText row={r} /></div>
                <div style={{ width: 170 }}><StatusBadge status={r.status} /></div>
              </div>
            </div>
            );
          })}
        </Card>
      )}
    </>
  );
}

/* ─── One request ──────────────────────────────────────────────────── */

function RequestView({ requestId }: { requestId: string }) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const threadEnd = useRef<HTMLDivElement>(null);

  const query = useQuery({
    queryKey: ['portal', 'remediation', requestId],
    queryFn: async () => (await portalApi.get(`${BASE}/detail`, { params: { request_id: requestId } })).data as Detail,
    refetchInterval: 30_000,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['portal', 'remediation'] });

  const send = useMutation({
    mutationFn: async () => (await portalApi.post(`${BASE}/message`, { request_id: requestId, body: draft.trim() })).data,
    onSuccess: () => { setDraft(''); setError(''); refresh(); },
    onError: err => setError(apiError(err, 'Could not send the message.')),
  });
  const submit = useMutation({
    mutationFn: async () => (await portalApi.post(`${BASE}/submit`, { request_id: requestId, note: note.trim() || null })).data,
    onSuccess: () => { setSubmitting(false); setNote(''); setError(''); refresh(); },
    onError: err => setError(apiError(err, 'Could not submit the request.')),
  });

  const messageCount = query.data?.messages.length ?? 0;
  // Keep the newest message in view by scrolling the thread itself, never the page.
  useEffect(() => { const box = threadEnd.current; if (box) box.scrollTop = box.scrollHeight; }, [messageCount]);

  // The server marks this recipient's last_seen_at on every detail fetch (the GET /detail handler
  // itself calls mark_remediation_recipient_seen -- see remediation_portal_routes.py). Patch the list
  // query's cache to match immediately, so the "N new" count on this page, the sidebar badge, and the
  // mobile nav dot all clear the moment the request is opened, rather than waiting for the list's next
  // refetch. Runs again on each 30s poll too, which just keeps re-stamping "now" -- harmless.
  useEffect(() => {
    if (!query.data) return;
    const seenAt = new Date().toISOString();
    const current = queryClient.getQueryData<RequestRow[]>(['portal', 'remediation']);
    if (current) {
      queryClient.setQueryData<RequestRow[]>(['portal', 'remediation'],
        current.map(x => (x.id === requestId ? { ...x, recipient_last_seen_at: seenAt } : x)));
    }
  }, [query.data, requestId, queryClient]);

  const back = (
    <Link to="/questionnaire/portal/remediation" className="pw-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: T.slate, textDecoration: 'none', fontWeight: 600 }}>
      <Icon d={IC.back} size={14} /> All remediation requests
    </Link>
  );

  if (query.isPending) return <Card><div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner size={28} /></div></Card>;
  if (!query.data) {
    return (
      <>
        <div style={{ marginBottom: 16 }}>{back}</div>
        <Card><EmptyState icon={IC.alert} title="This request can't be opened">
          {apiError(query.error, 'It may have been withdrawn, or it was sent to a different email address.')}
        </EmptyState></Card>
      </>
    );
  }

  const { request: r, risks, messages, events } = query.data;
  const fixed = risks.filter(x => x.status === 'fixed').length;
  const allFixed = risks.length > 0 && fixed === risks.length;
  const canSubmit = r.status === 'in_progress';

  const stat = (label: string, value: React.ReactNode, sub?: React.ReactNode) => (
    <div style={{ padding: '14px 18px' }}>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: T.ink, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  );

  return (
    <>
      <PageHeader
        breadcrumb={back}
        title={r.title}
        subtitle={<>From <strong style={{ color: T.ink2 }}>{r.sender_org_name}</strong> for {r.vendor_name} · received {fmtDate(r.created_at)}</>}
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <StatusBadge status={r.status} />
            {canSubmit && <Button icon={IC.send} onClick={() => { setError(''); setSubmitting(true); }}>Submit for review</Button>}
          </div>
        }
      />

      <div style={{ display: 'grid', gap: 18 }}>
        {r.status === 'awaiting_review' && <Alert tone="info" icon={IC.clock}>Submitted{r.submitted_at ? ` on ${fmtDate(r.submitted_at)}` : ''}. {r.sender_org_name} is reviewing your fixes and will complete the request or send it back.</Alert>}
        {r.status === 'completed' && <Alert tone="success">{r.sender_org_name} marked this request completed{r.completed_at ? ` on ${fmtDate(r.completed_at)}` : ''}. Thank you.</Alert>}
        {r.status === 'in_progress' && allFixed && <Alert tone="success">The latest scan confirms every risk fixed. Submit the request so {r.sender_org_name} can review it.</Alert>}
        {r.status === 'in_progress' && r.overdue && !allFixed && <Alert tone="warning">This request is past its due date. If you need more time, message {r.sender_org_name} below.</Alert>}
        {error && !submitting && <Alert tone="error">{error}</Alert>}

        <Card padding={0}>
          <div className="pw-tiles" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
            {stat('Due', r.due_date ? fmtDate(r.due_date) : 'No date', r.due_date && r.status === 'in_progress'
              ? (daysUntil(r.due_date) < 0 ? <span style={{ color: T.red, fontWeight: 700 }}>{-daysUntil(r.due_date)} days overdue</span> : `${daysUntil(r.due_date)} days left`) : undefined)}
            {stat('Risks fixed', `${fixed} of ${risks.length}`, 'confirmed by scans')}
            {stat('Your score now', r.current_score ?? '—', 'from their latest scan')}
            {stat('Once fixed', r.projected_score ?? '—', 'estimated')}
          </div>
        </Card>

        {r.message && (
          <Card>
            <div style={{ fontSize: 11.5, fontWeight: 600, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>Message from {r.sender_org_name}</div>
            <div style={{ fontSize: 14, color: T.ink2, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{r.message}</div>
          </Card>
        )}

        <Card padding={0}>
          <div style={{ padding: '18px 20px 0' }}>
            <CardHeader title="Risks to fix" subtitle="Each risk is marked fixed automatically once a scan no longer finds it. Open one to see what was found and how to fix it." />
          </div>
          {risks.map(risk => {
            const open = expanded === risk.id;
            const color = SEVERITY[risk.severity || ''] || T.slate;
            return (
              <div key={risk.id} style={{ borderTop: `1px solid ${T.bdr}` }}>
                <button onClick={() => setExpanded(open ? null : risk.id)} className="pw-row" style={{
                  display: 'grid', gridTemplateColumns: '96px minmax(0, 1fr) auto 16px', gap: 14, alignItems: 'center', width: '100%', padding: '13px 20px',
                  background: open ? T.surface2 : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: sans,
                }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />{risk.severity}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: T.ink }}>{risk.name}</span>
                    <span style={{ display: 'block', fontSize: 12.5, color: T.muted, marginTop: 1 }}>{risk.category || 'Other'}</span>
                  </span>
                  {risk.status === 'fixed'
                    ? <Pill color={T.green} bg={T.greenBg}>Fixed</Pill>
                    : <Pill color={T.slate} bg={T.hover}>Open</Pill>}
                  <span style={{ color: T.faint, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s', display: 'inline-flex' }}><Icon d={IC.chevRight} size={15} /></span>
                </button>
                {open && (
                  <div style={{ padding: '4px 20px 18px 130px', background: T.surface2, fontSize: 13.5, color: T.ink2, lineHeight: 1.65 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em' }}>What was found</div>
                    <div style={{ margin: '4px 0 12px' }}>{risk.description || 'No description recorded.'}</div>
                    {risk.recommendation && (
                      <>
                        <div style={{ fontSize: 11.5, fontWeight: 600, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em' }}>How to fix it</div>
                        <div style={{ margin: '4px 0 12px', padding: '10px 12px', borderRadius: 8, background: T.blueBg, border: '1px solid #BFDBFE', color: '#1E40AF' }}>{risk.recommendation}</div>
                      </>
                    )}
                    {risk.evidence && (
                      <>
                        <div style={{ fontSize: 11.5, fontWeight: 600, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em' }}>Evidence</div>
                        <pre style={{ margin: '4px 0 0', padding: 10, borderRadius: 8, background: T.surface, border: `1px solid ${T.bdr}`, fontSize: 12, color: T.slate,
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 220, overflow: 'auto', fontFamily: sans }}>{risk.evidence}</pre>
                      </>
                    )}
                    {risk.status === 'fixed' && risk.fixed_at && <div style={{ fontSize: 12.5, color: T.green, fontWeight: 600, marginTop: 10 }}>Confirmed fixed {fmtDate(risk.fixed_at)}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </Card>

        <div className="pw-grid-2" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)', gap: 18, alignItems: 'start' }}>
          <Card padding={0}>
            <div style={{ padding: '18px 20px 0' }}>
              <CardHeader title="Messages" subtitle={`Ask ${r.sender_org_name} a question or explain what you changed.`} />
            </div>
            <div ref={threadEnd} style={{ padding: '0 20px', display: 'grid', gap: 12, maxHeight: 420, overflowY: 'auto' }}>
              {messages.length === 0 && <div style={{ fontSize: 13, color: T.muted, padding: '8px 0 4px' }}>No messages yet.</div>}
              {messages.map(m => {
                const mine = m.author_type === 'vendor';
                return (
                  <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                    <div style={{ maxWidth: '85%' }}>
                      <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 3, textAlign: mine ? 'right' : 'left' }}>
                        {mine ? (m.author_name || 'You') : `${m.author_name || 'Reviewer'} · ${r.sender_org_name}`} · {fmtDateTime(m.created_at)}
                      </div>
                      <div style={{ padding: '10px 13px', borderRadius: 12, fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: T.ink2,
                        background: mine ? T.blueBg : T.surface2, border: `1px solid ${mine ? '#BFDBFE' : T.bdr}` }}>{m.body}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            {r.status !== 'completed' ? (
              <div style={{ padding: 16, display: 'flex', gap: 10, alignItems: 'flex-end', borderTop: `1px solid ${T.bdr}`, marginTop: 14 }}>
                <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={2} maxLength={5000} placeholder="Write a message…" className="pw-input"
                  onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && draft.trim()) send.mutate(); }}
                  style={{ flex: 1, boxSizing: 'border-box', padding: '10px 12px', fontSize: 14, fontFamily: sans, color: T.ink, background: T.surface,
                    border: `1px solid ${T.bdr}`, borderRadius: 8, outline: 'none', resize: 'vertical', lineHeight: 1.5 }} />
                <Button icon={IC.send} loading={send.isPending} disabled={!draft.trim()} onClick={() => send.mutate()}>Send</Button>
              </div>
            ) : <div style={{ height: 18 }} />}
          </Card>

          <Card>
            <CardHeader title="Activity" />
            <div>
              {events.map((e, i) => (
                <div key={e.id} style={{ display: 'flex', gap: 12 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', marginTop: 5, flexShrink: 0,
                      background: ['risk_fixed', 'all_fixed', 'completed'].includes(e.kind) ? T.green : e.kind === 'risk_reopened' ? T.red : T.bdrStrong }} />
                    {i < events.length - 1 && <span style={{ width: 2, flex: 1, minHeight: 16, background: T.bdr }} />}
                  </div>
                  <div style={{ paddingBottom: 14, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: T.ink2, wordBreak: 'break-word' }}>{EVENT_TEXT[e.kind] || e.kind}{e.kind.startsWith('risk_') && e.note ? `: ${e.note}` : ''}</div>
                    <div style={{ fontSize: 12, color: T.muted, marginTop: 1 }}>{fmtDateTime(e.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {submitting && (
        <div onMouseDown={e => { if (e.target === e.currentTarget && !submit.isPending) setSubmitting(false); }}
          style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div role="dialog" aria-label="Submit for review" style={{ background: T.surface, borderRadius: 14, width: 480, maxWidth: '100%', padding: 24, fontFamily: sans, animation: 'pwFadeIn .15s ease-out' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: T.ink }}>Submit for review?</div>
            <div style={{ fontSize: 13.5, color: T.muted, marginTop: 8, lineHeight: 1.6 }}>
              {allFixed
                ? `The latest scan confirms every risk fixed. ${r.sender_org_name} will review and complete the request.`
                : `${risks.length - fixed} risk${risks.length - fixed === 1 ? ' is' : 's are'} still open in their latest scan. If you've fixed ${risks.length - fixed === 1 ? 'it' : 'them'} since, say so below — a new scan will confirm it.`}
            </div>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} maxLength={5000} placeholder="Optional note: what you changed" className="pw-input"
              style={{ width: '100%', boxSizing: 'border-box', marginTop: 14, padding: '10px 12px', fontSize: 14, fontFamily: sans, color: T.ink, background: T.surface,
                border: `1px solid ${T.bdr}`, borderRadius: 8, outline: 'none', resize: 'vertical', lineHeight: 1.5 }} />
            {error && <div style={{ marginTop: 12 }}><Alert tone="error">{error}</Alert></div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
              <Button variant="secondary" onClick={() => setSubmitting(false)} disabled={submit.isPending}>Cancel</Button>
              <Button icon={IC.send} loading={submit.isPending} onClick={() => submit.mutate()}>Submit for review</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
